// px, not rem: the wordmark is a logo, which WCAG 1.4.4 exempts from text resizing. In
// rem it doubled at 200% text and ran past a 390px phone's edge (found 2026-10-15).
const SIZE_MAP = {
  sm: { img: "h-[32px] w-[32px]", fontSize: "20px" },
  md: { img: "h-[40px] w-[40px]", fontSize: "24px" },
  lg: { img: "h-[48px] w-[48px]", fontSize: "24px" },
} as const;

interface FomoFiLogoProps {
  /** Visual scale. sm ≈ form headers, md ≈ auth panels, lg ≈ landing nav/footer. */
  size?: "sm" | "md" | "lg";
  /** Hide the wordmark and render the icon only. */
  showText?: boolean;
  className?: string;
}

export default function FomoFiLogo({ size = "md", showText = true, className }: FomoFiLogoProps) {
  const { img, fontSize } = SIZE_MAP[size];
  return (
    <span className={`inline-flex items-center gap-2 ${className ?? ""}`}>
      {/* SVG, not PNG: web renders it natively, so the mark stays sharp at every
          size here and at any future one without a new raster export. The PNG it
          replaced was the same mark but soft — 5,268 distinct opaque colours and
          2,944 off-hue fringe pixels, against 106 and 0 for this file. Mobile
          cannot use this asset (no metro SVG transformer, and RN's Image cannot
          decode SVG on native), so mobile keeps a 512px PNG of the same vector. */}
      <img src="/fomofi-logo-v8.svg" alt="Fomo-Fi" className={img} />
      {showText && (
        // "FOMO" inherits the surrounding surface color via currentColor, so it
        // stays legible everywhere — dark on light surfaces, and light on dark
        // ones (e.g. the navy sidebar sets `text-sidebar-foreground`) without
        // hardcoding a colour or depending on the app's light/dark mode. The
        // "FI" accent is `brand-mark`, the mark's own blue, so the wordmark
        // matches the "F" beside it (it was `success` green until BEN-141). The
        // hyphen stays in currentColor with "FOMO" so the accent reads as the
        // "FI" syllable, not as a floating dash.
        // `whitespace-nowrap` is load-bearing: a hyphen is a valid line-break
        // opportunity, so without it the wordmark splits to "FOMO-" / "FI" on
        // narrow headers.
        // RD-13: the drawn wordmark is hidden from screen readers; the image's
        // alt already names the brand in its text spelling, "Fomo-Fi".
        <span
          aria-hidden="true"
          className="whitespace-nowrap text-current"
          style={{ fontSize, fontWeight: 500, letterSpacing: "0.14em" }}
        >
          FOMO-<span className="text-brand-mark">FI</span>
        </span>
      )}
    </span>
  );
}
